import React from "react";
import {
  FileText,
  Users,
} from "lucide-react";

const PrivacyPolicy = () => {
  const ContactCard = ({
    title,
    name,
    position,
    email,
    phone,
  }: {
    title: string;
    name: string;
    position: string;
    email: string;
    phone: string;
  }) => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
      <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
        <Users className="w-5 h-5 mr-2" />
        {title}
      </h4>
      <div className="space-y-2 text-sm">
        <p>
          <span className="font-medium text-gray-700">성명:</span> {name}
        </p>
        <p>
          <span className="font-medium text-gray-700">직위:</span> {position}
        </p>
        <p>
          <span className="font-medium text-gray-700">이메일:</span>
          <a
            href={`mailto:${email}`}
            className="text-blue-600 hover:text-blue-800 ml-2"
          >
            {email}
          </a>
        </p>
        <p>
          <span className="font-medium text-gray-700">전화:</span>
          <a
            href={`tel:${phone}`}
            className="text-blue-600 hover:text-blue-800 ml-2"
          >
            {phone}
          </a>
        </p>
      </div>
    </div>
  );

  const RemedyOrganization = ({
    name,
    phone,
    url,
  }: {
    name: string;
    phone: string;
    url: string;
  }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
      <h5 className="font-semibold text-gray-800 mb-2">{name}</h5>
      <div className="text-sm text-gray-600 space-y-1">
        <p>전화: {phone}</p>
        <p>
          URL:{" "}
          <a
            href={`https://${url}`}
            className="text-blue-600 hover:text-blue-800"
            target="_blank"
            rel="noopener noreferrer"
          >
            {url}
          </a>
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white font-serif-kr">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <FileText className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Aivy 개인정보처리방침
              </h2>
              <p className="text-sm text-gray-600 mb-2">
                <strong>시행일: 2025년 6월 1일</strong>
              </p>
              <p className="text-gray-700 leading-relaxed">
                주식회사 디에이블(이하 '회사')은 「개인정보 보호법」에 따라
                이용자의 개인정보를 보호하고 관련 고충을 신속하게 처리하기 위해
                다음과 같은 개인정보처리방침을 수립·공개합니다.
              </p>
            </div>
          </div>
        </div>

        <section className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            제1조 개인정보의 수집 및 이용
          </h3>

          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-4">
              1. 회원가입 및 서비스 이용
            </h4>
            <p className="text-gray-700 mb-4">
              회사는 Aivy 플랫폼 서비스 제공을 위해 다음의 개인정보를
              수집합니다.
            </p>

            <h5 className="text-sm font-semibold text-gray-800 mb-2">
              필수항목
            </h5>
            <table className="w-full border border-gray-300 mb-4">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    수집목적
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    수집항목
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    보유기간
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    회원 식별 및 서비스 제공
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    전화번호, 이메일
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    회원탈퇴 시까지
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    AI 에이전트 생성 및 관리
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    에이전트명, 설정정보
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    회원탈퇴 시까지
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    맞춤형 AI 서비스 제공
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    에이전트 실행 내역
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    회원탈퇴 시까지
                  </td>
                </tr>
              </tbody>
            </table>

            <h5 className="text-sm font-semibold text-gray-800 mb-2">
              선택항목
            </h5>
            <table className="w-full border border-gray-300 mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    수집목적
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    수집항목
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    보유기간
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    서비스 품질 향상
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    사용패턴, 선호도, 피드백
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    회원탈퇴 시까지
                  </td>
                </tr>
              </tbody>
            </table>

            <h4 className="text-md font-semibold text-gray-900 mb-4">
              2. 유료서비스 이용
            </h4>
            <table className="w-full border border-gray-300 mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    수집목적
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    수집항목
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    보유기간
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    결제 및 환불 처리
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    결제정보(카드번호 일부, 결제일시)
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    전자상거래법에 따라 5년
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    세금계산서 발행
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    사업자등록번호, 상호명
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    국세기본법에 따라 5년
                  </td>
                </tr>
              </tbody>
            </table>

            <h4 className="text-md font-semibold text-gray-900 mb-4">
              3. 마케팅 및 홍보 (선택)
            </h4>
            <table className="w-full border border-gray-300 mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    수집목적
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    수집항목
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    보유기간
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    이벤트 안내 및 혜택 제공
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    이메일, 휴대폰번호
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    동의철회 시까지
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    맞춤형 광고 제공
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    관심사, 서비스 이용내역
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    동의철회 시까지
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            제2조 (만 14세 미만 아동의 개인정보 처리)
          </h3>

          <div>
            <p className="text-gray-700 mb-2">
              1. 회사는 만 14세 미만 아동의 개인정보 수집 시 법정대리인의 동의를
              받습니다.
            </p>
            <p className="text-gray-700 mb-2">
              2. 교육 목적의 경우, 교사나 학부모의 동의하에 제한적 서비스를
              제공합니다.
            </p>
            <p className="text-gray-700 mb-4">
              3. 아동의 개인정보는 교육 및 안전한 AI 체험에 필요한 최소한만
              수집합니다.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            제3조 (개인정보의 처리 위탁)
          </h3>

          <div>
            <p className="text-gray-700 mb-4">
              원활한 서비스 제공을 위해 다음과 같이 개인정보 처리를 위탁합니다:
            </p>

            <table className="w-full border border-gray-300 mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    수탁업체
                  </th>
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm">
                    위탁업무
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    AWS/Google Cloud
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    데이터 저장 및 서버 운영
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    (주)KG이니시스
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    결제 처리 및 정산
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    Solapi
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    카카오 알림톡 전송
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            제4조 (개인정보의 제3자 제공)
          </h3>

          <div>
            <p className="text-gray-700 mb-4">
              회사는 원칙적으로 이용자의 개인정보를 외부에 제공하지 않습니다.
              다만, 다음의 경우는 예외로 합니다:
            </p>
            <div>
              <p className="text-gray-700 mb-2">
                1. 이용자가 사전에 동의한 경우
              </p>
              <p className="text-gray-700 mb-2">
                2. 법령의 규정에 의거하거나 수사기관의 요구가 있는 경우
              </p>
              <p className="text-gray-700 mb-2">
                3. 통계작성, 학술연구를 위해 특정 개인을 식별할 수 없는 형태로
                제공하는 경우
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            제5조 (개인정보의 보유 및 파기)
          </h3>

          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                1. 보유기간
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>회원정보: 회원탈퇴 시까지</li>
                <li>법령에 따른 보존: 관련 법령에서 정한 기간</li>
                <li>AI 학습데이터: 익명화 후 서비스 개선 목적으로 활용</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                2. 파기절차
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>탈퇴 후 복구 불가능한 방법으로 완전 삭제</li>
                <li>법령에 따라 보존이 필요한 정보는 별도 분리 보관</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                3. 장기 미이용 회원 관리
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>1년간 서비스 미이용 시 사전 안내</li>
                <li>안내 후 30일 내 미접속 시 휴면계정 전환</li>
                <li>휴면 후 5년 경과 시 자동 파기</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            제6조 (개인정보 자동 수집 및 거부)
          </h3>

          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-3">
              1. 로그 기록
            </h4>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>수집항목: IP주소, 접속시간, 서비스 이용기록</li>
              <li>보유기간: 3개월 (통신비밀보호법)</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            제7조 (개인정보의 안전성 확보 조치)
          </h3>

          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                1. 기술적 보호조치
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>SSL 인증서를 통한 데이터 암호화 전송</li>
                <li>비밀번호 단방향 암호화 저장</li>
                <li>방화벽 및 침입탐지시스템 운영</li>
                <li>주기적 보안 취약점 점검</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                2. 관리적 보호조치
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>개인정보 접근권한 최소화</li>
                <li>직원 대상 정기 보안교육 실시</li>
                <li>개인정보처리시스템 접속기록 관리</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            제8조 (정보주체의 권리와 행사방법)
          </h3>

          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                1. 이용자의 권리
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>개인정보 열람권</li>
                <li>정정·삭제 요구권</li>
                <li>처리정지 요구권</li>
                <li>동의철회권</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                2. 권리 행사 방법
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>온라인: 고객센터 &gt; 1:1 문의</li>
                <li>
                  이메일:{" "}
                  <a
                    href="mailto:dplanit@naver.com"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    dplanit@naver.com
                  </a>
                </li>
                <li>
                  전화:{" "}
                  <a
                    href="tel:070-8805-3009"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    070-8805-3009
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                3. 법정대리인의 권리
              </h4>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>
                  만 14세 미만 아동의 개인정보에 대한 열람, 정정, 삭제 요구 가능
                </li>
                <li>신분증 사본 등 법정대리인 확인 서류 제출 필요</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            제9조 (개인정보 보호책임자)
          </h3>

          <div>
            <p className="text-gray-700 mb-6">
              개인정보 처리 관련 문의사항이나 불만사항은 아래로 연락주시기
              바랍니다.
            </p>

            <ContactCard
              title="개인정보 보호책임자"
              name="소지영"
              position="최고정보보호책임자, 대표자(CISO/CEO)"
              email="dplanit@naver.com"
              phone="070-8805-3009"
            />
          </div>
        </section>

        <section className="mb-12">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            제10조 (권익침해 구제방법)
          </h3>

          <div>
            <p className="text-gray-700 mb-6">
              개인정보 침해로 인한 구제를 받으시려면 아래 기관에 문의하실 수
              있습니다:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <RemedyOrganization
                name="개인정보보호위원회 개인정보 침해신고센터"
                phone="(국번없이) 118"
                url="privacy.kisa.or.kr"
              />
              <RemedyOrganization
                name="개인정보 분쟁조정위원회"
                phone="1833-6972"
                url="www.kopico.go.kr"
              />
              <RemedyOrganization
                name="대검찰청 사이버수사과"
                phone="(국번없이) 1301"
                url="www.spo.go.kr"
              />
              <RemedyOrganization
                name="경찰청 사이버수사국"
                phone="(국번없이) 182"
                url="ecrm.cyber.go.kr"
              />
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            제11조 (개인정보처리방침의 변경)
          </h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-gray-700">
                1. 이 개인정보처리방침은 2025년 6월 1일부터 적용됩니다.
              </p>
              <p className="text-gray-700">
                2. 변경사항은 시행 7일 전 홈페이지 및 이메일로 공지합니다.
              </p>
              <p className="text-gray-700">
                3. 중요한 변경사항은 30일 전 사전공지하며, 필요시 동의를 다시
                받습니다.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h5 className="font-semibold text-gray-800 mb-2">개정이력</h5>
              <p className="text-sm text-gray-600">
                2025.06.01 - 개인정보처리방침 제정
              </p>
            </div>
          </div>
        </section>

        <div className="border-t border-gray-200 pt-8 mt-12">
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              본 개인정보처리방침에 대한 문의사항은{" "}
              <a
                href="tel:070-8805-3009"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                070-8805-3009
              </a>
              로 연락주시기 바랍니다.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
